import {
  faBolt,
  faCommentsQuestionCheck,
  faDumbbell,
  faHeadSideBrain,
  faMessagesQuestion,
  faUserGroup,
} from '@fortawesome/pro-solid-svg-icons'

import type {
  ProgramBenefit,
  ProgramEmailData,
  ProgramWorldSignUpCard,
} from '@/client/services/programs/types'

import type { Stat } from '@/components/Landing/StatsCard'

export const KA_LEARNING_LABS_TUTOR_LANDING_PAGE_REF = 'KA-LEARNING-LABS-TUTOR-LANDING-PAGE'
export const KA_LEARNING_LABS_LEARNER_LANDING_PAGE_REF = 'KA-LEARNING-LABS-LEARNER-LANDING-PAGE'

export const KA_LEARNING_LABS_TUTOR_FAQ_URL =
  'https://docs.google.com/document/d/1gArGmbT4Kn_2qQlkgvornvjh69HMmqNtCwhG9Uq0BWw/edit?usp=sharing'
export const KA_LEARNING_LABS_LEARNER_FAQ_URL =
  'https://docs.google.com/document/d/1gArGmbT4Kn_2qQlkgvornvjh69HMmqNtCwhG9Uq0BWw/edit?usp=sharing'

export const ALGEBRA2_LEARNING_LABS_ITERATION_SLUGS = [
  'november-2024',
  'february-2025',
  'march-2025',
  '2025-spring-recruitment-1',
  '2025-spring-recruitment-2',
  '2025-spring-recruitment-3',
] as const

export const PRE_CALCULUS_LEARNING_LABS_ITERATION_SLUGS = [
  'november-2024',
  'february-2025',
  'march-2025',
  'april-2025',
] as const

export const KA_LEARNING_LABS_SIGN_UP_CARD_LEARNER: ProgramWorldSignUpCard = {
  signUpCardHeading: 'Register',
  signUpCardText: 'Join one of our programs and receive personalized math help.',
}

export const KA_LEARNING_LABS_SIGN_UP_CARD_TUTOR: ProgramWorldSignUpCard = {
  signUpCardHeading: 'Host a Khan Academy Learning Lab',
  signUpCardText: 'Shape futures and gain valuable experience as a tutor.',
}

const REVIEW_AND_REINFORCE_BULLET_TEXT = 'Review and reinforce key skills with your tutor'
const PRACTICE_AND_CONQUER_BULLET_TEXT = 'Practice for and conquer Khan Academy assessments'
const GET_PERSONALIZED_MATH_SUPPORT_BULLET_TEXT = 'Get personalized math support'
const STAY_MOTIVATED_BULLET_TEXT = 'Stay motivated and accountable on your road to mastery'

const EMPOWER_STUDENTS_BULLET_TEXT =
  'Empower your students in a 4-week course to master units of Khan Academy'
export const PROVIDE_PERSONALIZED_TUTORING_BULLET_TEXT =
  'Provide personalized tutoring based on your own learning experience'
export const EARN_VOLUNTEER_HOURS_BULLET_TEXT =
  'Earn volunteer hours and boost your college application'

export const KA_LEARNING_LABS_WHAT_YOULL_DO_BULLET_PROPS_LEARNER = [
  {
    key: 'review-reinforce',
    icon: faUserGroup,
    body: REVIEW_AND_REINFORCE_BULLET_TEXT,
  },
  {
    key: 'practice-conquer',
    icon: faDumbbell,
    body: PRACTICE_AND_CONQUER_BULLET_TEXT,
  },
  {
    key: 'personalized-support',
    icon: faCommentsQuestionCheck,
    body: GET_PERSONALIZED_MATH_SUPPORT_BULLET_TEXT,
  },
  {
    key: 'stay-motivated',
    icon: faBolt,
    body: STAY_MOTIVATED_BULLET_TEXT,
  },
]

export const KA_LEARNING_LABS_WHAT_YOULL_DO_BULLET_PROPS_TUTOR = [
  {
    key: 'empower-students',
    icon: faUserGroup,
    body: EMPOWER_STUDENTS_BULLET_TEXT,
  },
  {
    key: 'personalized-tutoring',
    icon: faCommentsQuestionCheck,
    body: PROVIDE_PERSONALIZED_TUTORING_BULLET_TEXT,
  },
  {
    key: 'earn-volunteer-hours',
    icon: faBolt,
    body: EARN_VOLUNTEER_HOURS_BULLET_TEXT,
  },
]

export const KA_LEARNING_LABS_PROGRAM_TUTOR_BENEFITS: ProgramBenefit[] = [
  {
    id: 'empower-students',
    text: EMPOWER_STUDENTS_BULLET_TEXT,
    faIcon: faUserGroup,
    iconProps: { color: 'cyan.solid', size: 'lg' },
  },
  {
    id: 'join-community',
    text: 'Join a global community of more than 11,000 tutors',
    faIcon: faHeadSideBrain,
    iconProps: { color: 'yellow.solid', size: 'lg' },
  },
  {
    id: 'personalized-tutoring',
    text: PROVIDE_PERSONALIZED_TUTORING_BULLET_TEXT,
    faIcon: faMessagesQuestion,
    iconProps: { color: 'pink.solid', size: 'lg' },
  },
  {
    id: 'earn-volunteer-hours',
    text: EARN_VOLUNTEER_HOURS_BULLET_TEXT,
    faIcon: faBolt,
    iconProps: { color: 'green.solid', size: 'lg' },
  },
]

export const KA_LEARNING_LABS_LEARNER_STATS: Stat[] = [
  {
    multiplier: 4,
    heading: 'more learning',
    text: 'Learners spent significantly more time on Khan Academy with the support and accountability of a tutor',
  },
  {
    percentage: 40,
    heading: 'more mastery',
    text: 'Students mastered skills at a 58% rate in their learning labs compared to a 42% rate before tutoring',
  },
  {
    percentage: 97,
    heading: 'satisfaction',
    text: 'The tutoring sessions were highly beneficial with students rating 97% of sessions as helpful or super helpful',
  },
]

export const KA_LEARNING_LABS_PROGRAM_LEARNER_BENEFITS: ProgramBenefit[] = [
  {
    id: 'review-reinforce',
    text: REVIEW_AND_REINFORCE_BULLET_TEXT,
    faIcon: faUserGroup,
    iconProps: { color: 'cyan.solid', size: 'lg' },
  },
  {
    id: 'practice-conquer',
    text: PRACTICE_AND_CONQUER_BULLET_TEXT,
    faIcon: faHeadSideBrain,
    iconProps: { color: 'yellow.solid', size: 'lg' },
  },
  {
    id: 'personalized-support',
    text: GET_PERSONALIZED_MATH_SUPPORT_BULLET_TEXT,
    faIcon: faMessagesQuestion,
    iconProps: { color: 'pink.solid', size: 'lg' },
  },
  {
    id: 'stay-motivated',
    text: STAY_MOTIVATED_BULLET_TEXT,
    faIcon: faBolt,
    iconProps: { color: 'green.solid', size: 'lg' },
  },
]

export const KA_LEARNING_LABS_EMAIL_DATA: ProgramEmailData = {
  programManagerFirstName: 'Rebecca',
  tutorFaqUrl: KA_LEARNING_LABS_LEARNER_FAQ_URL,
  learnerFaqUrl: KA_LEARNING_LABS_TUTOR_FAQ_URL,
}
